/* eslint-disable array-callback-return */
import AddIcon from '@mui/icons-material/Add';
import { Button, Divider, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { cloneDeep, concat, get, set } from 'lodash';
import React, { FC, useEffect, useMemo, useState } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import MUIAccordionComponent from 'src/components/ui-component/mui-accordion/MUIAccordionComponent';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import { useAuthentication } from 'src/context/AuthContext';
import DeleteRecord from 'src/lib/api/DeleteRecord';
import GetRecords from 'src/lib/api/GetRecords';
import { APIS } from 'src/lib/constants/apis';
import {
	ALS_ERLEDIGT_MARKIEREN_LABEL,
	ANGABEN_ZUM_VORSTEUERABZUG_ORGANISATION,
	ANGABEN_ZUM_VORSTEUERABZUG_STIPENDIUM_ANTRAG,
	ERLAUTERUNG_IN_KOSTEN_TITLE,
	FORM_FIELDS_TYPES,
	KOSTEN_ARTEN,
	KOSTEN_POSITION,
	KOSTENTRAEGER,
	PARTNER_DATEN_NICHT_ERFASST,
	PLATZHALTER_TEXT,
} from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { ForceTrue } from 'src/lib/validation/ValidationTypes';
import PositionLiegtNichtVor from 'src/pages/private/online-antrag/antrag-components/PositionLiegtNichtVor';
import { checkIfSpecificKostenPositionHasComment } from 'src/pages/private/online-antrag/application-helper-function/PruefAssistenzHelperFunktion';
import { withKostenkategorieEinsatzbereichFilter } from 'src/utils/einsatzbereichHelper';
import { getFormStrucComponentTable } from 'src/utils/formStruc';
import { addIndexesToPath, checkReadOnlyInUeberarbeitung, getOptionsFromRes, removeDataKeys, removeNullDeep } from 'src/utils/helper';
import PruefAssistenz from '../../../../../antrag-components/PruefAssistenz';
import ApplicationHeading from '../../../../../application-heading/ApplicationHeading';
import {
	checkIfPruefAssitenzNeedsToValidate,
	getBasisDataKostenKategorie,
	getKostenErlauterung,
} from '../../../../../application-helper-function/AppHelperfunctions';

// import schemas
import Schema from 'src/lib/paths/antrag_FULL_Paths.json';
import kostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
const P = Schema.attributes;
const KP_P = kostenPositionSchema.attributes;

const SachkostenAntragStipendium: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const { authenticatedData } = useAuthentication();
	const { application, setApplicationProperty, readOnly, saveApplication } = useApplication();
	const kostentraeger = KOSTENTRAEGER.Antragsteller;
	const unternehmenId = get(authenticatedData, 'organisationId');
	const [kostenKategorie, setKostenKategorie] = useState([]);
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const handleAddSachkosten = async () => {
		let appCopy = cloneDeep(application);

		const data = getBasisDataKostenKategorie(
			appCopy,
			kostenKategorie, // All kostenkategorie for Sachkosten, to find default Kostenkategorie id
			kostentraeger,
			unternehmenId,
			KOSTEN_POSITION.Ausgabe,
			KOSTEN_ARTEN.Sachkosten,
		);
		set(
			appCopy,
			addIndexesToPath(
				P.Foerderantrag_Kostenpositions.path,
				[`${get(application, P.Foerderantrag_Kostenpositions.path, []).length}`],
				['Foerderantrag_Kostenpositions'],
			),
			{ attributes: data },
		);
		saveApplication(false, appCopy);
	};
	const handleDeleteSachKosten = async (id: number) => {
		try {
			let appcopy = cloneDeep(application);
			const allKostenposition = get(appcopy, P.Foerderantrag_Kostenpositions.path, []);
			set(
				appcopy,
				P.Foerderantrag_Kostenpositions.path,
				allKostenposition.filter((item: any) => item.id !== id),
			);
			let res = await DeleteRecord(APIS.Kostenposition, id);
			if (res) {
				saveApplication(false, appcopy);
			}
		} catch (err: any) {
			console.error('ERROR: Deleting Personalkosten', err);
		}
	};

	const showHinweisVorsteuerabzugWiderspricht = useMemo(() => {
		const orgaVorsteuerabzug = get(
			application,
			P.Antragstellende_Organisation.schema.attributes.Unternehmensprofil_Angaben_Zum_Vorsteuerabzug.path,
		);
		const sachkostenVorsteuerabzug = get(application, P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Vorsteuerberechtigung.path);

		// Wenn einer der Werte leer/nicht gesetzt ist, return false
		if (!orgaVorsteuerabzug || !sachkostenVorsteuerabzug) {
			return false;
		}

		if (
			(orgaVorsteuerabzug === ANGABEN_ZUM_VORSTEUERABZUG_ORGANISATION.Vorsteuerabzugsberechtigt &&
				sachkostenVorsteuerabzug === ANGABEN_ZUM_VORSTEUERABZUG_STIPENDIUM_ANTRAG.Vorsteuerabzugsberechtigt) ||
			(orgaVorsteuerabzug === ANGABEN_ZUM_VORSTEUERABZUG_ORGANISATION.keineVorsteuerabzugsberechtigung &&
				sachkostenVorsteuerabzug === ANGABEN_ZUM_VORSTEUERABZUG_STIPENDIUM_ANTRAG.keineVorsteuerabzugsberechtigung)
		) {
			return false;
		}

		return true;
	}, [application]);

	// Fetch KostenKategories
	useEffect(() => {
		const fetch = async () => {
			try {
				const filter = withKostenkategorieEinsatzbereichFilter(
					{
						Allgemein_Kostenposition: KOSTEN_POSITION.Ausgabe,
						Allgemein_Kostenart_Finanzierung: KOSTEN_ARTEN.Sachkosten,
						Allgemein_Antragsformular: {
							$contains: get(application, P.Allgemein_Antragsformular.path),
						},
						Allgemein_Aktiv: { $eq: true },
					},
					APIS.Antrag,
				);
				let res = await GetRecords(APIS.Kostenkategorie, [], filter);
				setKostenKategorie(removeNullDeep(removeDataKeys(get(res, 'data', []))));
			} catch (err: any) {
				console.error('ERROR: FEtching Kosten kategorie ', err);
			}
		};
		fetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<ApplicationHeading title='Sachkosten' description={PLATZHALTER_TEXT} />
			<PositionLiegtNichtVor kostenPosition={KOSTEN_POSITION.Ausgabe} kostenArt={KOSTEN_ARTEN.Sachkosten} updatedReadOnly={updatedReadOnly} />
			<Divider sx={{ borderBottomWidth: 1, borderColor: 'black', mt: 2, mb: 1 }} />
			<Typography variant='h6' fontWeight={'bold'}>
				Zu den förderfähigen Sachausgaben gehören beispielsweise:
			</Typography>
			<ul>
				<li>
					<Typography variant='body1' gutterBottom>
						Anschaffungen
					</Typography>
				</li>
				<li>
					<Typography variant='body1' gutterBottom>
						Arbeitsmaterialien
					</Typography>
				</li>
				<li>
					<Typography variant='body1' gutterBottom>
						Auftragsvergütungen
					</Typography>
				</li>{' '}
				<li>
					<Typography variant='body1' gutterBottom>
						Miete und ähnliche laufende Kosten des Unternehmens
					</Typography>
				</li>
			</ul>
			<Typography variant='body1' gutterBottom sx={{ mb: 2 }}>
				Die Verwendung des Stipendiums zur Schuldentilgung oder zur Finanzierung von Verpflichtungen, die vor Projektbeginn eingegangen wurden –
				z. B. Zahlungen für vorher getätigte Anschaffungen –, ist ausgeschlossen. Ebenfalls ausgeschlossen sind Reisekosten.
			</Typography>
			<Divider sx={{ borderBottomWidth: 1, borderColor: 'black', mt: 1, mb: 2 }} />
			<Typography variant='h6' sx={{ mb: 3, mt: 1 }} fontWeight={'bold'}>
				Erklärung zum Vorsteuerabzug
			</Typography>
			<Typography>
				<strong>Bitte beachten Sie:</strong> Bei Vorsteuerabzugsberechtigung sind die Sachkosten als <u>Netto-Beträge</u> anzusetzen. Um die von
				Ihnen angegebenen Kosten korrekt zu bewerten, geben Sie bitte hier an, was auf die von Ihnen gemachten Angaben und Ihren Status zutrifft:
			</Typography>

			{showHinweisVorsteuerabzugWiderspricht && (
				<MUIAlert
					infoType={'warning'}
					message={
						'Die hier abgegebene Erklärung zum Vorsteuerabzug widerspricht den von Ihnen gemachten Angaben im Organisationsprofil. Bitte überprüfen Sie Ihre Angaben und korrigieren Sie diese an der betreffenden Stelle.'
					}
				/>
			)}

			<CreateForm formStruc={vorsteuerStruc()} formValues={application} updateFormValuesFunc={setApplicationProperty} readOnly={updatedReadOnly} />
			<Typography sx={{ mt: 2 }} fontWeight={'bold'}>
				Sollten sich während der Projektlaufzeit Änderungen an der Vorsteuerabzugsberechtigung ergeben, so sind diese der Bewilligungsbehörde
				unverzüglich mitzuteilen.
			</Typography>
			<Divider sx={{ borderBottomWidth: 1, borderColor: 'black', mt: 2, mb: 3 }} />
			{get(application, P.Foerderantrag_Kostenpositions.path, []).map((ele: any, i: number) => {
				if (
					get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe &&
					get(ele, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Sachkosten &&
					get(ele, `${KP_P.Unternehmen.path}.id`) === unternehmenId
				) {
					return (
						<MUIAccordionComponent
							key={i}
							summaryTitle={`Sachkosten | ${get(ele, KP_P.Allgemein_Bezeichnung.path, '')}`}
							onDelete={() => handleDeleteSachKosten(get(ele, 'id'))}
							showDeleteButton={!updatedReadOnly}
							hasComment={checkIfSpecificKostenPositionHasComment(readOnly, ele)}
						>
							<PruefAssistenz
								statusPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Pruefstatus.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								hinweisPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Hinweis_Fuer_Antragsteller.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								ueberarbeitetAmPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Ueberarbeitet_Am.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								erledigtPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								formStruc={mapSachKosten(PruefAssistenzStruc(), i)}
							/>

							<MUIAlert
								title={ERLAUTERUNG_IN_KOSTEN_TITLE}
								message={getKostenErlauterung(kostenKategorie, get(ele, KP_P.Allgemein_Kostenkategorie.path))}
							/>

							<Grid2 spacing={6} container>
								<Grid2 sm={6}>
									<CreateForm
										formStruc={mapSachKosten(formStruc1(kostenKategorie), i)}
										formValues={application}
										updateFormValuesFunc={setApplicationProperty}
										readOnly={updatedReadOnly}
										api={APIS.Kostenposition}
										size='small'
									/>
								</Grid2>
								<Grid2 sm={6}>
									<CreateForm
										formStruc={mapSachKosten(kostenProJahrStruc(application, i), i)}
										formValues={application}
										updateFormValuesFunc={setApplicationProperty}
										readOnly={updatedReadOnly}
										api={APIS.Kostenposition}
									/>
								</Grid2>
							</Grid2>
						</MUIAccordionComponent>
					);
				}
			})}
			{unternehmenId ?
				<Button sx={{ mt: '2rem' }} endIcon={<AddIcon />} disabled={updatedReadOnly} onClick={handleAddSachkosten}>
					Sachkosten hinzufügen
				</Button>
			:	<MUIAlert message={PARTNER_DATEN_NICHT_ERFASST} />}
		</>
	);
};

export default SachkostenAntragStipendium;

const vorsteuerStruc = () => {
	return [
		{
			fields: [
				{
					...P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Vorsteuerberechtigung,
					type: FORM_FIELDS_TYPES.radio,
					direction: 'column',
					label: '',
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
};

const PruefAssistenzStruc = () => {
	return [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];
};
const formStruc1 = (kostenKategorie?: any[]) => {
	const kategorieOptions = kostenKategorie || [];
	return [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kostenkategorie,
					type: 'singleSelect',
					options: getOptionsFromRes(kategorieOptions, 'Allgemein_Bezeichnung'),
				},
				P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Bezeichnung,
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
};
const formStruc2 = () => [
	{
		fields: [
			P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Bezeichnung,
			{
				...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Geplante_Gesamtsumme,
				format: 'currency',
				disabled: true,
			},
		],
		userProps: { direction: 'column', columnToDivide: 1 },
	},
];
const kostenProJahrStruc = (application: any, i: number) => [
	{
		fields: [
			{
				...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kosten_Pro_Jahr,
				fieldProps: { Zelle_2: { format: 'currency' } },
				disabledColumns: ['Zelle_1'],
				type: 'componentTable',
				tableLabel: 'Ausgaben pro Jahr',
				maxLevel: get(application, P.Allgemein_Projektdauerin_Jahren.path),
				hideDeleteButton: true,
				sumRow: [
					{
						id: 'pinnedRow',
						Zelle_1: 'Gesamt',
						Zelle_2: get(
							application,
							addIndexesToPath(
								P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Geplante_Gesamtsumme.path,
								[`${i}`],
								['Foerderantrag_Kostenpositions'],
							),
						),
					},
				],
			},
		],
		userProps: { columnToDivide: 1 },
	},
];

const mapSachKosten = (formStruc: any[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				return {
					...value,
					path: addIndexesToPath(get(value, 'path', ''), [`${index}`], ['Foerderantrag_Kostenpositions']),
				};
			}),
		};
	});
	return result;
};

export function getFormStrucSachkostenAntragStipendium(application: any, unternehmenId?: number): FormStrucItem[] {
	let formStruc: FormStrucItem[] = [];

	get(application, P.Foerderantrag_Kostenpositions.path, []).forEach((ele: any, i: number) => {
		if (
			get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe &&
			get(ele, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Sachkosten &&
			get(ele, `${KP_P.Unternehmen.path}.id`) === unternehmenId
		) {
			const isHinweisFieldRequired = checkIfPruefAssitenzNeedsToValidate(ele, KP_P.Pruefung_Pruefstatus.path, KP_P.Pruefung_Erledigt.path);

			const formStrucCTWithIndex = getFormStrucComponentTable(application, mapSachKosten(kostenProJahrStruc(application, i), i));

			const formStruc1WithIndex = mapSachKosten(formStruc1(), i);
			const formStruc2WithIndex = mapSachKosten(formStruc2(), i);

			if (isHinweisFieldRequired) {
				formStruc = concat(mapSachKosten(PruefAssistenzStruc(), i), formStruc, formStruc1WithIndex, formStruc2WithIndex, vorsteuerStruc);
			} else {
				formStruc = concat(formStruc, formStruc1WithIndex, formStruc2WithIndex, formStrucCTWithIndex, vorsteuerStruc());
			}
		}
	});
	return formStruc;
}
